import React, { useCallback, useEffect, useState } from "react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { debounce, exchangeDate, formatDate, getPractice, notify, numberWithCommas } from "../../../helpers";
import { CalendarDays, CircleX, Search, ServerCrash } from "lucide-react";
import DataTablePagination from "../../DataTablePagination";
import ErrorLogsDataTable from "../../DataTable/ErrorLogsDataTable";
import { fetchErrorLogs, fetchIndividualError } from "../../../services/errorLogs";
import DatePicker from "react-datepicker";
import ErrorLogsViewModal from "../../Modal/ErrorLogsViewModal";

export default function ErrorLogs({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [eligibilityLogsData, setEligibilityLogsData] = useState([]);
  const [dateRange, setDateRange] = useState(["", ""]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [viewLogData, setViewLogData] = useState({});
  const [viewLogModalOpen, setViewLogModalOpen] = useState(false);
  const [openedFromUrlId, setOpenedFromUrlId] = useState(null);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    search_term: "",
    start_date: "",
    end_date: "",
    account_id: account_id,
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const docpro_get_query_param = (key) => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const v = (sp.get(key) || "").trim();
      return v;
    } catch {
      return "";
    }
  };

  const docpro_remove_query_param = (key) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(key);
      window.history.replaceState({}, "", url.toString());
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runAsLogView = async () => {
      const rawId = docpro_get_query_param("id");
      if (!rawId) return;

      if (String(openedFromUrlId || "") === String(rawId) && viewLogModalOpen) return;

      const error_id = Number(rawId);
      if (!Number.isFinite(error_id) || error_id <= 0) return;

      setDataLoading(true);

      try {
        const response = await fetchIndividualError(account_id, error_id);
        
        if (!isMounted) return;

        if(response){
          if(response?.data?.status === 'success'){
            setViewLogData(response.data.result);
            setPreviousScrollPosition(window.scrollY || 0);
            setOpenedFromUrlId(error_id);
            setViewLogModalOpen(true);
          } else {
            notify("error", response?.data?.message, 0.5);
          }
        }
      } catch (e) {
        docpro_remove_query_param("id");
        setOpenedFromUrlId(null);
      } finally {
        if (isMounted) setDataLoading(false);
      }
    };

    runAsLogView();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id]);

  useEffect(() => {
    // when modal closes
    if (!viewLogModalOpen && openedFromUrlId) {
      docpro_remove_query_param("id");
      setOpenedFromUrlId(null);
    }
  }, [viewLogModalOpen, openedFromUrlId]);

  useEffect(() => {
    loadErrorLogsData(filter);
  }, [filter]);

  const loadErrorLogsData = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchErrorLogs(filter);
      if (response) {
        if(response?.data){
          setEligibilityLogsData(response?.data?.results || []);
          setPaginationCount({
            totalCount: response?.data?.count,
            totalPages: response?.data.total_pages,
          });
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadErrorLogsData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleDateChange = (update) => {
    const [start, end] = update;

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    }
    setDataLoading(true)

    setDateRange([start, end]);
  };

  useEffect(() => {
    const sameAccount = filter.account_id === account_id;
    if (sameAccount) return;

    setFilter({
      ...filter,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id]);

  const channel = new BroadcastChannel('tab_practice_change');
  useEffect(() => {
    channel.onmessage = (event) => {
      setFilter({
        ...filter,
        location_id: event?.data?.location_id,
        account_id: event?.data?.account_id,
      });
      account_id = event?.data?.account_id;
    };
    return () => {
      channel.close();
    };
  }, [channel, account_id]);

  useDocumentTitle("Error Logs");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24" style={{ marginBottom: "0px" }}>
          <i className="docpro_blue_bg docpro_white_color">
            <ServerCrash width={20} height={20} />
          </i>
          Error Logs
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="5" sm="5" />

      <div className="docpro_data_table_wrap">
        <div className={`docpro_data_table_headings small_heading docpro_grid_gap_10 ${filter.start_date === filter.end_date ? "docpro_have_date_filter" : "" }`}>
          <div className={`searching_div docpro_grid_gap_10 ${(filter.start_date && (filter.start_date === filter.end_date) && (paginationCount.totalCount > 0) ) ? "docpro_sm_height_lg_42" : ""}`}>
            <form className="docpro_search_form">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            <div className={`flex items-center docpro_grid_gap_4 docpro_date_filter`}>
              <div className={`docpro_toggle_calendar ${filter.start_date ? "active" : "" }`}>
                <CalendarDays />

                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}
              </div>
              {filter.start_date && (
                <span onClick={() => {
                  setFilter({
                    ...filter,
                    start_date: "",
                    end_date: "",
                  });
                  setDateRange([null, null]);
                  setDataLoading(true);
                }}>
                  <CircleX size={22} color="var(--accent)" className="pointer" />
                </span>
              )}
            </div>
          </div>
        </div>

        <ErrorLogsDataTable
          tableData={eligibilityLogsData}
          reloadData={loadErrorLogsData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          setViewLogModalOpen={setViewLogModalOpen}
          setViewLogData={setViewLogData}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(eligibilityLogsData.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {viewLogModalOpen &&
        <ErrorLogsViewModal
          logData={viewLogData}
          activeModal={viewLogModalOpen}
          setActiveModal={setViewLogModalOpen}
          previousScrollPosition={previousScrollPosition}
        />
      }
    </>
  );
}
